import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import trads from '~/intl/trads.json'
import Breadcrumb from '~/components/core/breadcrumb'
import Wysiwyg from '~/components/core/Wysiwyg/Wysiwyg'
import ButtonArrow from '~/components/core/buttonArrow'
import ProfileNav from '~/components/features/profile/profileNav'
import VideoPlayer from '~/components/slices/videoPlayer'
import { Calendar } from '~/icons/Calendar'
import { Pin } from '~/icons/Pin'
import { Clock } from '~/icons/Clock'
import { setHasFixedProfileNav } from '~/store/actions'
import eventDate from '~/utils/eventDate'
import { contentModel } from '~/utils/contentModel'
import transformLink from "~/utils/transformLink";

const Hero = ({
  data,
  currentUrl,
  bgColor,
  hasAnchors = false,
  type = null,
  subType = null,
  customType = null,
  map = false,
  newsroomProfile = false,
  video = null,
}) => {
  const [hasMounted, setHasMounted] = React.useState(false)
  const lang = useSelector((state) => state.lang)
  const hasFixedProfileNav = useSelector((state) => state.hasFixedProfileNav)
  const dispatch = useDispatch()

  let imageUrl = null

  if (data.mainImage) {
    imageUrl = data.mainImage.fields
      ? data.mainImage.fields.file.fields.file.url
      : data.mainImage.file && data.mainImage.file.file
        ? data.mainImage.file.file.url
        : null
  }
  const chapo = data.chapo

  const breadcrumbLabel = data.breadcrumbCurrentLabel
    ? data.breadcrumbCurrentLabel
    : data.title

  const mapRiverains = map === 'Riverains'
  const mapProjects = map === 'Riverains' || map === 'Activités'

  const dateOptions = { year: 'numeric', month: 'short', day: 'numeric' }

  let hasProfileNav = data.profile || newsroomProfile ? true : false

  if (type && (type === contentModel.news || type === 'event')) {
    hasProfileNav = false
  }

  const renderEvent = () => {
    const date = eventDate(
      data.startDate,
      data.endDate,
      trads?.[lang]?.date?.from,
      trads?.[lang]?.date?.to,
      trads?.[lang]?.date?.the,
      lang,
    )

    return (
      <>
        <p className="inline-flex w-full items-center title-4-semi-bold">
          <Calendar className="icon-white mr-4" width={24} height={24} />
          {date}
        </p>
        {data.location && (
          <p className="inline-flex w-full items-center title-4-semi-bold mt-4">
            <Pin className="icon-white mr-4" width="24" height="24" />
            {data.location.location ? data.location.location : data.location}
          </p>
        )}

        {data.schedule && (
          <div className="inline-flex w-full items-center title-4-semi-bold mt-4">
            <Clock className="icon-white mr-4" width="24" height="24" />
            <Wysiwyg
              document={data.schedule.raw ? data.schedule.raw : data.schedule}
              noClass={true}
            />
          </div>
        )}
      </>
    )
  }

  const renderRegular = (type) => {
    let pdf, annexes

    if (data.pressRelease) {
      data.pressRelease = data.pressRelease.fields
        ? data.pressRelease.fields
        : data.pressRelease
      pdf = data.pressRelease.pdf?.fields
        ? data.pressRelease.pdf.fields
        : data.pressRelease.pdf

      if (data.pressRelease.annexes) {
        annexes = data.pressRelease.annexes.fields
          ? data.pressRelease.annexes.fields
          : data.pressRelease.annexes
      }
    }

    if (data.document) {
      data.document = data.document.fields
        ? data.document.fields
        : data.document
    }

    const pdfFile = pdf && pdf.file ? pdf.file.url : null
    const linkAnnexes = annexes && annexes.file ? annexes.file.url : null
    const documentUrl =
      data && data.document && data.document.file
        ? data.document.file.url
        : null

    let cta: any = null

    if (data.chapoCta) {
      cta = data.chapoCta.fields ? data.chapoCta.fields : data.chapoCta;
    }

    const { url, contentTypeContentful } = transformLink(cta, true, false)

    return (
      <>
        <Wysiwyg
          document={chapo}
          classes={`hero__chapo ${map !== false && 'hidden md:block'}`}
        />

        {cta && (
            <div className="md:flex mt-8">
              <ButtonArrow
                  text={cta.label}
                  link={url}
                  contentType={contentTypeContentful}
                  color="purple"
              />
            </div>
        )}

        {data.pressRelease && (
            <div className="md:flex mt-12">
              <ButtonArrow
                  text={trads[lang].newsroom.newsPressRelease}
                  link={pdfFile}
                  external="true"
              color="purple"
            />

            {annexes && (
              <div className="mt-6 md:mt-0 md:ml-10">
                <ButtonArrow
                  text={trads[lang].newsroom.newsAnnexes}
                  link={linkAnnexes}
                  external="true"
                  color="purple"
                  icon="dl"
                />
              </div>
            )}
          </div>
        )}

        {data.document && !data.pressRelease && (
          <div className="mt-12">
            <ButtonArrow
              text={trads[lang].newsroom.newsDocument}
              link={documentUrl}
              external="true"
              color="purple"
            />
          </div>
        )}
      </>
    )
  }

  useEffect(() => {
    setHasMounted(true)
    dispatch(setHasFixedProfileNav(false))
  }, [dispatch])

  return (
    <div
      className={`hero ${mapProjects ? 'hero--map' : ''} ${mapRiverains ? 'hero--map-riverains' : ''} ${hasAnchors ? 'has-anchors' : ''} bg-${bgColor}`}
    >
      {imageUrl !== null && (
        <picture className="hero__picture w-full">
          <source
            media="(min-width: 768px)"
            srcSet={`${imageUrl}?fit=fill&f=center&w=1440&h=400&fm=jpg&fl=progressive, ${imageUrl}?fit=fill&f=center&w=2880&h=800&fm=jpg&fl=progressive 2x`}
          />
          <source
            media="(max-width: 415px)"
            srcSet={`${imageUrl}?fit=fill&f=faces&w=415&h=253&fm=jpg&fl=progressive, ${imageUrl}?fit=fill&f=faces&w=830&h=506&fm=jpg&fl=progressive 2x`}
          />
          <source
            media="(max-width: 767px)"
            srcSet={`${imageUrl}?fit=fill&f=center&w=720&h=440&fm=jpg&fl=progressive, ${imageUrl}?fit=fill&f=center&w=1440&h=880&fm=jpg&fl=progressive 2x`}
          />
          <img
            src={`${imageUrl}?fit=fill&f=center&w=1440&h=400&fm=jpg&fl=progressive`}
            srcSet={`${imageUrl}?fit=fill&f=center&w=1440&h=400&fm=jpg&fl=progressive, ${imageUrl}?fit=fill&f=center&w=2880&h=800&fm=jpg&fl=progressive 2x`}
            className="w-full"
          />
        </picture>
      )}

      <div
        className={`hero__content ${map === 'Raccordement' ? 'hero__content--connection' : ''} row `}
      >
        <div className="fluid-grid">
          <div className="col-12 lg-col-9 xl-col-8 lg-col-offset-1 xl-col-offset-2">
            <Breadcrumb
              currentUrl={currentUrl}
              links={data.breadcrumb ? data.breadcrumb : null}
              current={breadcrumbLabel}
              lightColor={bgColor === 'light-blue' ? true : false}
              type={type}
              withProfileNav={hasProfileNav && hasMounted}
              customType={customType}
            />
          </div>
        </div>

        {hasProfileNav && hasMounted && (
          <ProfileNav
            title={
              data.profile
                ? data.profile.fields
                  ? data.profile.fields.title
                  : data.profile.title
                : newsroomProfile
            }
          />
        )}

        <div
          className={`fluid-grid hero__bottom-content ${hasFixedProfileNav ? 'has-fixed-nav' : ''}`}
        >
          <div
            className={`col-12 ${type === 'simulator' ? 'lg-col-8 xl-col-7' : 'lg-col-9 xl-col-8'} lg-col-offset-1 xl-col-offset-2 ${hasProfileNav && hasMounted ? 'pt-8 md:pt-13' : ''}`}
          >
            {type === contentModel.news && (
              <p className="title-block mb-1">{`${trads[lang].date.the} ${new Date(data.publicationDate).toLocaleDateString(lang, dateOptions)}`}</p>
            )}

            <h1 className={`hero__title`}>{data.title}</h1>

            <div className="col-12 lg-col-8 xl-col-7">
              {type === 'event' ? renderEvent() : renderRegular(type)}

              {video ? (
                <div className="mt-6 md:mt-10 -mb-11 md:-mb-16 lg:mb-0">
                  <VideoPlayer
                    data={video}
                    isProject={false}
                    isWysiwyg={true}
                    profile="null"
                  />
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Hero
